import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as fromEntities from '@app/core/entities/reducers';
import { Allocation, Asset, Project, TelemetryEventTypes } from '@app/core/models';
import { BackendService, SmartRiskService } from '@app/core/services';
import { CsvExportService } from '@app/core/services/csv-export.service';
import { ConfirmationModalComponent } from '@app/shared/components/confirmation-modal/confirmation-modal.component';
import { AppActions } from '@app/store/actions';
import * as fromRoot from '@app/store/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { combineLatest, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { ProjectsActions } from '../actions';

@Injectable()
export class ProjectEffects {
  readonly actions$ = inject(Actions);
  readonly store = inject<Store<fromRoot.State>>(Store);
  readonly router = inject(Router);
  readonly smartRiskService = inject(SmartRiskService);
  readonly backendService = inject(BackendService);
  readonly dialog = inject(MatDialog);
  readonly csvExportService = inject(CsvExportService);

  addProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.addProject),
      withLatestFrom(this.store.select(fromEntities.getAllAssets)),
      switchMap(([action, assets]) =>
        this.backendService.addProject(action.project).pipe(
          switchMap(addedProject => {
            const initedProject: Project = this._getInitiatedProject(addedProject, action.project, assets);

            const actionsToDispatch: Action[] = [ProjectsActions.addProjectSuccess({ project: initedProject })];
            if (action.selectAddedProject === true) {
              actionsToDispatch.push(ProjectsActions.setSelectedProject({ selectedProject: addedProject }));
            }
            if (action.runExAnteAnalysis === true) {
              actionsToDispatch.push(ProjectsActions.getExAnteAnalysis({ project: addedProject }));
            }

            return actionsToDispatch;
          }),
          catchError(error => {
            this.store.dispatch(ProjectsActions.addProjectFailure());
            return throwError(error);
          })
        )
      )
    );
  });

  updateProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.updateProject),
      withLatestFrom(this.store.select(fromEntities.getAllAssets)),
      switchMap(([action, assets]) =>
        this.backendService.updateProject(action.project).pipe(
          map(updatedProject => {
            const initedProject: Project = this._getInitiatedProject(updatedProject, action.project, assets);

            return ProjectsActions.updateProjectSuccess({
              project: { id: initedProject.id, changes: initedProject },
            });
          }),
          catchError(error => {
            this.store.dispatch(ProjectsActions.updateProjectFailure());
            return throwError(error);
          })
        )
      )
    );
  });

  upsertProjectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.addProjectSuccess, ProjectsActions.updateProjectSuccess),
      map(() => AppActions.httpSuccess({ messageKey: { key: 'success.save-project' } }))
    );
  });

  upsertProjectFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.addProjectFailure, ProjectsActions.updateProjectFailure),
      map(() => AppActions.httpFailure({ messageKey: { key: 'errors.save-project' } }))
    );
  });

  addProjectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.addProjectSuccess),
      map(action =>
        AppActions.sendTelemetryEvent({
          telemetryData: {
            eventType: TelemetryEventTypes.projectCreation,
            tags: { financialProfile: action.project.financialProfile ? action.project.financialProfile : '' },
          },
        })
      )
    );
  });

  exportAllocationAsCsv$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.exportAllocationAsCsv),
        withLatestFrom(this.store.select(fromEntities.getAllAssets), this.store.select(fromRoot.getCurrentLang)),
        switchMap(([{ allocationIndex }, assets, currentLang]) => {
          return this.store.pipe(
            select(fromEntities.getSelectedProject),
            filter(selectedProject => !!selectedProject),
            take(1),
            tap(selectedProject => {
              this.csvExportService.exportAllocationAsCsv(
                selectedProject.allocations[allocationIndex],
                assets,
                currentLang
              );
            })
          );
        })
      ),
    { dispatch: false }
  );

  updateProjectArchiveStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.updateProjectArchiveStatus),
      withLatestFrom(this.store.select(fromEntities.getAllAssets)),
      switchMap(([action, assets]) =>
        this.backendService.updateProject(action.project).pipe(
          map(updatedProject => {
            const initedProject: Project = this._getInitiatedProject(updatedProject, action.project, assets);

            return ProjectsActions.updateProjectArchiveStatusSuccess({
              project: { id: initedProject.id, changes: initedProject },
            });
          }),
          catchError(error => {
            this.store.dispatch(ProjectsActions.updateProjectFailure());
            return throwError(error);
          })
        )
      )
    );
  });

  updateProjectArchiveStatusSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.updateProjectArchiveStatusSuccess),
      map(({ project }) => {
        const translationKey = project.changes.isArchived ? 'success.archive-project' : 'success.unarchive-project';
        return AppActions.httpSuccess({ messageKey: { key: translationKey } });
      })
    );
  });

  saveSelectedProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.saveSelectedProject),
      withLatestFrom(this.store.select(fromEntities.getSelectedProject)),
      map(([_action, selectedProject]) =>
        selectedProject.id
          ? ProjectsActions.updateProject({ project: selectedProject })
          : ProjectsActions.addProject({ project: selectedProject })
      )
    );
  });

  saveSelectedProjectAs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.saveSelectedProjectAs),
      withLatestFrom(this.store.select(fromEntities.getSelectedProject)),
      map(([{ newProjectName, runExAnteAnalysisAfterCreation }, selectedProject]) =>
        ProjectsActions.addProject({
          project: { ...selectedProject, id: undefined, name: newProjectName },
          selectAddedProject: true,
          runExAnteAnalysis: runExAnteAnalysisAfterCreation,
        })
      )
    );
  });

  resetSelectedProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.resetSelectedProject),
      withLatestFrom(
        this.store.select(fromEntities.getAllProjects),
        this.store.select(fromEntities.getSelectedProject)
      ),
      switchMap(([{ route }, projects, selectedProject]) => {
        const savedSelectedProject = projects.find(project => selectedProject && project.id === selectedProject.id);
        if (
          isEqual(this._removeUnsavedProperties(savedSelectedProject), this._removeUnsavedProperties(selectedProject))
        ) {
          return of(ProjectsActions.confirmResetSelectedProject({ route }));
        }
        return this.dialog
          .open(ConfirmationModalComponent, {
            panelClass: 'am-dialog',
            width: '50%',
            maxWidth: '600px',
            minWidth: '400px',
            data: {
              titleKey: 'shared.unsaved-project-dialog.title',
              content: { key: 'shared.unsaved-project-dialog.content' },
              confirmLabelKey: 'shared.unsaved-project-dialog.confirm-label',
              cancelLabelKey: 'shared.unsaved-project-dialog.cancel-label',
            },
          })
          .afterClosed()
          .pipe(
            switchMap(confirmValue =>
              confirmValue
                ? of(ProjectsActions.confirmResetSelectedProject({ route }))
                : of(ProjectsActions.cancelResetSelectedProject())
            )
          );
      })
    );
  });

  confirmResetSelectedProject$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.confirmResetSelectedProject),
        filter(({ route }) => route != null),
        tap(({ route }) => this.router.navigate([route]))
      ),
    { dispatch: false }
  );

  loadUserProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.loadUserProjects),
      withLatestFrom(this.store.select(fromRoot.getCurrentUser)),
      switchMap(([_, currentUser]) =>
        combineLatest(
          this.backendService.getUserProjects(currentUser.nameId),
          this.store.pipe(
            select(fromEntities.getAllAssets),
            filter(assets => assets && assets.length > 0),
            take(1)
          )
        ).pipe(
          map(([userProjects, assets]) => {
            userProjects.forEach(project => {
              project.missingAssets = this._getProjectMissingAssets(project.allocations, assets);
              project.missingFinancialProfile = this._getProjectMissingFinancialProfiles(
                project.financialProfile,
                assets
              );
            });
            return ProjectsActions.loadUserProjectsSuccess({ projects: userProjects });
          }),
          catchError(error => {
            this.store.dispatch(ProjectsActions.loadUserProjectsFailure());
            return throwError(error);
          })
        )
      )
    );
  });

  loadUserProjectsFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.loadUserProjectsFailure),
      map(() => AppActions.httpFailure({ messageKey: { key: 'errors.get-projects' } }))
    );
  });

  deleteProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.deleteProject),
      switchMap(action => {
        return this.backendService.deleteProject(action.projectId).pipe(
          map(projectId => ProjectsActions.deleteProjectSuccess({ projectId })),
          catchError(error => {
            this.store.dispatch(AppActions.httpFailure({ messageKey: { key: 'errors.delete-project' } }));
            return throwError(error);
          })
        );
      })
    );
  });

  deleteProjectSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.deleteProjectSuccess),
      map(_action => AppActions.httpSuccess({ messageKey: { key: 'success.delete-project' } }))
    );
  });

  getExAnteAnalysis$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ProjectsActions.getExAnteAnalysis),
      withLatestFrom(this.store.select(fromEntities.getAllAssets)),
      tap(() => this.router.navigate(['analysis'])),
      switchMap(([action, assets]) => {
        return this.smartRiskService.getExAnteAnalysis(action.project, assets).pipe(
          map(analysisResult => {
            action.project.allocations.forEach(allocation => {
              allocation.exAnteAnalysis = analysisResult.find(
                analysisItem => analysisItem.allocationId === allocation.id
              );
            });
            return ProjectsActions.getExAnteAnalysisSuccess({ selectedProject: action.project });
          }),
          catchError(error => {
            this.store.dispatch(ProjectsActions.getExAnteAnalysisFailure());
            return throwError(error);
          })
        );
      })
    );
  });

  getExAnteAnalysisFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectsActions.getExAnteAnalysisFailure),
        tap(() => this.router.navigate(['simulation']))
      ),
    { dispatch: false }
  );

  private _getInitiatedProject(addOrUpdateProject: Project, actionProject: Project, assets: Asset[]): Project {
    return {
      ...addOrUpdateProject,
      allocations: actionProject.allocations,
      missingAssets: this._getProjectMissingAssets(addOrUpdateProject.allocations, assets),
      missingFinancialProfile: this._getProjectMissingFinancialProfiles(addOrUpdateProject.financialProfile, assets),
      backtestResults: actionProject.backtestResults,
    };
  }

  private _getProjectMissingAssets(allocations: Allocation[], assets: Asset[]): boolean {
    for (const allocation of allocations) {
      if (!allocation.positions || allocation.positions.length === 0) {
        return true;
      }
      if (allocation.positions.find(position => !assets.find(asset => asset.id === position.instrumentId))) {
        return true;
      }
    }
    return false;
  }

  private _getProjectMissingFinancialProfiles(financialProfile: string, assetsList: Asset[]): boolean {
    const financialProfiles = assetsList[0].profiles.filter(profile => !profile.value.includes('-HEDGE'));
    return (
      financialProfiles.length !== 0 &&
      financialProfiles.every(financialProfileItem => financialProfileItem.value !== financialProfile)
    );
  }

  private _removeUnsavedProperties(project: Project): Project {
    return project?.allocations
      ? {
          ...project,
          allocations: project.allocations.map(allocationItem => {
            const allocation = { ...allocationItem };
            delete allocation.exAnteAnalysis;
            return allocation;
          }),
          backtestParameters: project.backtestParameters ? { ...project.backtestParameters } : undefined,
          backtestResults: undefined,
        }
      : project;
  }
}
